import { useState, useCallback } from 'react';
import axios from 'axios';

interface ProcessorResult {
  success: boolean;
  processor_name: string;
  original_size: number;
  processed_size: number;
  output_format: string;
  processing_time: number;
  metadata: Record<string, any>;
  filename: string;
  error_message?: string;
}

export const useImageProcessor = () => {
  const [file, setFile] = useState<File | null>(null);
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [result, setResult] = useState<ProcessorResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const processImage = useCallback(async (processorName: string, processorSettings: Record<string, any>) => {
    if (!file) return;

    setIsProcessing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // 构建查询参数
      const params = new URLSearchParams();
      Object.entries(processorSettings).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await axios.post(
        `/api/tools/image-processor/processor/process/${processorName}?${params.toString()}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || '处理失败');
    } finally {
      setIsProcessing(false);
    }
  }, [file]);

  const downloadProcessedImage = useCallback(async (processorName: string, processorSettings: Record<string, any>) => {
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      // 构建查询参数
      const params = new URLSearchParams();
      Object.entries(processorSettings).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await axios.post(
        `/api/tools/image-processor/processor/process/${processorName}/download?${params.toString()}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          responseType: 'blob',
        }
      );

      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = result?.filename || 'processed_image';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('下载失败');
    }
  }, [file, result]);

  const clearResult = useCallback(() => {
    setResult(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setFile(null);
    setSettings({});
    setResult(null);
    setError(null);
    setIsProcessing(false);
  }, []);

  return {
    file,
    setFile,
    settings,
    setSettings,
    result,
    isProcessing,
    error,
    processImage,
    downloadProcessedImage,
    clearResult,
    clearError,
    reset
  };
};